import { useState } from "react";
import { Autocomplete, Box, Button, Dialog, FormControl, TextField } from "@mui/material";
import { Cell, CellValueCountsView, MigrateTextCellValueForCellDocument } from "generated/graphql";
import { P } from "theme/typography";
import { LoadingButton } from "@mui/lab";
import { Close } from "@mui/icons-material";
import RequestStatus from "redux/slices/RequestStatus";
import { useSnackbar } from "notistack";
import { runGqlMutation } from "gqlClient";

type TextCellValueMigrateDailogProps = {
  onClose: () => void;
  cell: Cell;
  onSuccess: () => void;
  cellValueCounts: CellValueCountsView[];
};

const TextCellValueMigrateDailog = ({ onClose, cell, onSuccess, cellValueCounts }: TextCellValueMigrateDailogProps) => {
  const [status, setStatus] = useState<RequestStatus>(RequestStatus.IDLE);
  const { enqueueSnackbar } = useSnackbar();
  const [oldValue, setOldValue] = useState<string>("");
  const [newValue, setNewValue] = useState<string>("");

  const startMigration = (cellId: number, oldValue: string, newValue: string) => {
    setStatus(RequestStatus.PENDING);
    runGqlMutation(MigrateTextCellValueForCellDocument, {
      cellId,
      values: [{ oldValue, newValue }],
    })
      .then(() => {
        setStatus(RequestStatus.SUCCEEDED);
        enqueueSnackbar(`'${oldValue}' successfully renamed to '${newValue}'.`, {
          variant: "success",
        });
        onSuccess();
      })
      .catch(() => {
        setStatus(RequestStatus.FAILED);
        enqueueSnackbar(`Operation failed`, {
          variant: "error",
        });
      });
  };

  return (
    <Dialog
      open={true}
      onClose={() => {
        onClose();
      }}>
      <Box
        sx={{
          padding: 2,
          width: 500,
          "& > * + *": {
            marginTop: 1,
          },
        }}>
        <Box
          sx={{
            marginBottom: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}>
          <Box
            sx={{
              "& > * + *": {
                marginTop: 1,
              },
            }}>
            <P variant="xlBold">Rename Existing Response Values</P>
            <br />
            <P variant="xs">Select a value, then enter the value that replaces it.</P>
          </Box>
          <Box>
            <Button
              color="secondary"
              onClick={() => {
                onClose();
              }}>
              <Close />
            </Button>
          </Box>
        </Box>
        <Box
          sx={{
            "& > * + *": {
              marginTop: 2,
            },
          }}>
          <FormControl fullWidth disabled={status === RequestStatus.PENDING} size="small">
            <Autocomplete
              disablePortal
              freeSolo
              value={oldValue}
              onChange={(_evt, v) => {
                if (v) setOldValue(typeof v === "string" ? v : v.value);
              }}
              options={
                cellValueCounts
                  ?.filter((v) => {
                    return v.value.textValue !== null;
                  })
                  ?.map((v) => {
                    return {
                      label: v.value.textValue + " (" + v.count + ")",
                      value: v.value.textValue || "",
                    };
                  }) || []
              }
              size="small"
              renderInput={(params) => <TextField {...params} label="Old Value" />}
            />
          </FormControl>
          <TextField
            label="New value"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            fullWidth
            disabled={status === RequestStatus.PENDING}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}>
          <LoadingButton
            variant="contained"
            color="primary"
            loading={status === RequestStatus.PENDING}
            disabled={status === RequestStatus.PENDING || newValue === "" || oldValue === ""}
            onClick={() => {
              startMigration(cell.id, oldValue, newValue);
            }}>
            Rename Existing Responses
          </LoadingButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default TextCellValueMigrateDailog;
